import type { HTMLAttributes } from "react";
import React from "react";
import type { RangeCalendarState } from "react-stately";
import type { AriaButtonProps } from "react-aria";
import { VisuallyHidden, useDateFormatter } from "react-aria";
import ButtonIconV2 from "../ButtonIconV2";
import {
  ArrowLeftDoubleLine,
  ArrowLeftSLine,
  ArrowRightDoubleLine,
  ArrowRightSLine,
} from "../IconV2/react";
import { CalendarHeader as CalendarHeaderWrapper, CalendarTitle } from "../DatePicker/Calendar";
import { styled } from "@linaria/react";
import { colors } from "../Theme";

const StyledCalendarTitle = styled(CalendarTitle)`
  flex: 1 1 0%;
  text-align: center;
`;

const StyledCalendarHeaderWrapper = styled(CalendarHeaderWrapper)`
  width: 100%;
`;

const Spacer = styled.div`
  width: 24px;
  height: 1px;
`;

const StyledButtonIconV2 = styled(ButtonIconV2)`
  &:focus {
    box-shadow:
      0 0 0 1px ${colors.lightNitrogen500_MainFade075},
      0 0 0 2px ${colors.lightNitrogen500_MainFade060};
  }
`;

interface P {
  state: RangeCalendarState;
  calendarProps: HTMLAttributes<HTMLElement>;
  prevButtonProps: AriaButtonProps<"button">;
  nextButtonProps: AriaButtonProps<"button">;
}

function CalendarHeader({ state, calendarProps, prevButtonProps, nextButtonProps }: P) {
  const formatter = useDateFormatter({
    month: "short",
    year: "numeric",
    timeZone: state.timeZone,
  });

  delete prevButtonProps.onFocusChange;
  delete nextButtonProps.onFocusChange;
  delete prevButtonProps.onPress;
  delete nextButtonProps.onPress;

  const handlePrevButtonClick = state.focusPreviousPage;
  const handleNextButtonClick = state.focusNextPage;

  const handleNextYearButtonClick = React.useCallback(() => {
    state.focusNextSection(true);
  }, [state]);

  const handlePrevYearButtonClick = React.useCallback(() => {
    state.focusPreviousSection(true);
  }, [state]);

  return (
    <StyledCalendarHeaderWrapper>
      {/* Add a screen reader only description of the entire visible range rather than
       * a separate heading above each month grid. This is placed first in the DOM order
       * so that it is the first thing a touch screen reader user encounters.
       * In addition, VoiceOver on iOS does not announce the aria-label of the grid
       * elements, so the aria-label of the Calendar is included here as well. */}
      <VisuallyHidden>
        <h2>{calendarProps["aria-label"]}</h2>
      </VisuallyHidden>
      <div>
        {!state.isPreviousVisibleRangeInvalid() && (
          <>
            <StyledButtonIconV2
              onClick={e => {
                e.stopPropagation();
                handlePrevYearButtonClick();
              }}
              aria-label="Année Précédente"
            >
              <ArrowLeftDoubleLine />
            </StyledButtonIconV2>
            <StyledButtonIconV2
              {...prevButtonProps}
              onClick={e => {
                e.stopPropagation();
                handlePrevButtonClick();
              }}
            >
              <ArrowLeftSLine />
            </StyledButtonIconV2>
          </>
        )}
      </div>
      <StyledCalendarTitle aria-hidden type="body1SemiBold">
        {formatter.format(state.visibleRange.start.toDate(state.timeZone))}
      </StyledCalendarTitle>
      <Spacer />
      <StyledCalendarTitle aria-hidden type="body1SemiBold">
        {formatter.format(state.visibleRange.start.add({ months: 1 }).toDate(state.timeZone))}
      </StyledCalendarTitle>
      <div>
        {!state.isNextVisibleRangeInvalid() && (
          <>
            <StyledButtonIconV2
              className="focus-enabled"
              {...nextButtonProps}
              onClick={e => {
                e.stopPropagation();
                handleNextButtonClick();
              }}
            >
              <ArrowRightSLine />
            </StyledButtonIconV2>
            <StyledButtonIconV2
              onClick={e => {
                e.stopPropagation();
                handleNextYearButtonClick();
              }}
              aria-label="Année Précédente"
            >
              <ArrowRightDoubleLine />
            </StyledButtonIconV2>
          </>
        )}
      </div>
    </StyledCalendarHeaderWrapper>
  );
}

export default CalendarHeader;
